import React from 'react';
import { Switch, Select, InputNumber, Tag } from 'antd';
import { Card } from './styled';

const HOURS = Array.from({ length: 24 }, (_, i) => ({
  value: i,
  label: `${i.toString().padStart(2, '0')}:00`
}));

const TIMEZONES = [
  'America/Chicago', 'America/New_York', 'America/Denver', 'America/Los_Angeles', 'UTC'
].map((v) => ({ value: v, label: v }));

const StepSettings = ({ automation, update }) => {
  const settings = automation.settings || {};
  const setSetting = (key, patch) => update({
    settings: { ...settings, [key]: { ...(settings[key] || {}), ...patch } }
  });

  const quiet = settings.quietHours || {};
  const enroll = settings.enrollment || {};
  const exec = settings.execution || {};
  const rate = settings.rateLimits || {};
  const channels = settings.channels || {};

  return (
    <>
      <Card>
        <h3>Quiet Hours</h3>
        <div className="desc">Pause sending during overnight hours so contacts don&apos;t get late-night messages.</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
          <Switch
            checked={!!quiet.enabled}
            onChange={(v) => setSetting('quietHours', { enabled: v })}
          />
          <span>Enable quiet hours</span>
        </div>
        <div className="row">
          <div>
            <label>Start hour</label>
            <Select
              style={{ width: '100%' }}
              disabled={!quiet.enabled}
              value={quiet.startHour ?? 21}
              onChange={(v) => setSetting('quietHours', { startHour: v })}
              options={HOURS}
            />
          </div>
          <div>
            <label>End hour</label>
            <Select
              style={{ width: '100%' }}
              disabled={!quiet.enabled}
              value={quiet.endHour ?? 8}
              onChange={(v) => setSetting('quietHours', { endHour: v })}
              options={HOURS}
            />
          </div>
          <div className="full">
            <label>Timezone</label>
            <Select
              style={{ width: '100%' }}
              disabled={!quiet.enabled}
              value={quiet.timezone || 'America/Chicago'}
              onChange={(v) => setSetting('quietHours', { timezone: v })}
              options={TIMEZONES}
            />
          </div>
        </div>
      </Card>

      <Card>
        <h3>Enrollment & Deduplication</h3>
        <div className="desc">Decide what should happen when a contact triggers this automation more than once.</div>
        <div className="row">
          <div>
            <label>Enrollment policy</label>
            <Select
              style={{ width: '100%' }}
              value={enroll.policy || 'once'}
              onChange={(v) => setSetting('enrollment', { policy: v })}
              options={[
                { value: 'once', label: 'Only enroll once' },
                { value: 'always', label: 'Enroll every time the trigger fires' },
                { value: 'cooldown', label: 'Enroll again after a cooldown' }
              ]}
            />
          </div>
          <div>
            <label>If already enrolled</label>
            <Select
              style={{ width: '100%' }}
              value={enroll.onAlreadyEnrolled || 'skip'}
              onChange={(v) => setSetting('enrollment', { onAlreadyEnrolled: v })}
              options={[
                { value: 'skip', label: 'Skip the new trigger' },
                { value: 'restart', label: 'Restart the sequence' },
                { value: 'queue', label: 'Queue and run after current finishes' }
              ]}
            />
          </div>
          <div className="full" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <Switch
              checked={!!enroll.blockIfInOther}
              onChange={(v) => setSetting('enrollment', { blockIfInOther: v })}
            />
            <span>Don&apos;t enroll if contact is in another active automation</span>
          </div>
        </div>
      </Card>

      <Card>
        <h3>Execution Limits</h3>
        <div className="row">
          <div>
            <label>Max messages per contact</label>
            <InputNumber
              min={0}
              style={{ width: '100%' }}
              value={exec.maxPerContact ?? 1}
              onChange={(v) => setSetting('execution', { maxPerContact: v })}
            />
          </div>
          <div>
            <label>Cooldown between sends (hours)</label>
            <InputNumber
              min={0}
              style={{ width: '100%' }}
              value={exec.cooldownHours ?? 24}
              onChange={(v) => setSetting('execution', { cooldownHours: v })}
            />
          </div>
        </div>
      </Card>

      <Card>
        <h3>Rate Limiting</h3>
        <div className="desc">Use 0 to leave a limit unlimited.</div>
        <div className="row">
          <div>
            <label>Max concurrent runs</label>
            <InputNumber min={0} style={{ width: '100%' }}
              value={rate.maxConcurrent ?? 0}
              onChange={(v) => setSetting('rateLimits', { maxConcurrent: v })} />
          </div>
          <div>
            <label>Max sends per hour</label>
            <InputNumber min={0} style={{ width: '100%' }}
              value={rate.maxPerHour ?? 0}
              onChange={(v) => setSetting('rateLimits', { maxPerHour: v })} />
          </div>
          <div>
            <label>Max sends per day</label>
            <InputNumber min={0} style={{ width: '100%' }}
              value={rate.maxPerDay ?? 0}
              onChange={(v) => setSetting('rateLimits', { maxPerDay: v })} />
          </div>
          <div>
            <label>Stagger between sends (seconds)</label>
            <InputNumber min={0} style={{ width: '100%' }}
              value={rate.staggerSeconds ?? 0}
              onChange={(v) => setSetting('rateLimits', { staggerSeconds: v })} />
          </div>
        </div>
      </Card>

      <Card>
        <h3>Enabled Channels</h3>
        <div className="desc">Email is fully supported. SMS and Phone Call nodes will be saved but skipped until those integrations land.</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <Switch checked={channels.email !== false} onChange={(v) => setSetting('channels', { email: v })} />
            <span>Email</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <Switch checked={!!channels.sms} onChange={(v) => setSetting('channels', { sms: v })} />
            <span>SMS</span>
            <Tag color="orange">Coming Soon</Tag>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <Switch checked={!!channels.phone} onChange={(v) => setSetting('channels', { phone: v })} />
            <span>Phone Call</span>
            <Tag color="orange">Coming Soon</Tag>
          </div>
        </div>
      </Card>
    </>
  );
};

export default StepSettings;
